import { miQsWeatherDomains } from "@/constants/domain.config";
import { checkTestHost, flatOmit, isInDomainMap } from "@/utils";
import { isShowNews1 } from "@/utils/isShowNews1";
import { flatten, omit } from "lodash";
import { getCurrentAdScenario } from "../useAdScenario";

/** 判断当前 host 是否在指定域名列表中（含测试域名） */
export function shouldRenderAdForDomains(hostName: string, domains: string[]): boolean {
  if (!Array.isArray(domains) || domains.length === 0 || typeof hostName !== "string") {
    return false;
  }
  return checkTestHost(domains) || domains.includes(hostName);
}

/** 非 wi 域名广告展示条件 */
export function shouldRenderNonWiAd(): boolean {
  return checkTestHost(flatOmit(domainMap, ["wi"])) || isInDomainMap(domainMap, ["wi"]);
}

/** wi 域名专属广告展示条件（非新闻首页） */
export function shouldShowWiAdOnly(): boolean {
  return (
    !isShowNews1().validMain &&
    (checkTestHost([...domainMap["wi"]]) || domainMap["wi"].includes(window.location.hostname))
  );
}

/** 内容区通用广告瀑布流配置（miQs → tran → dex → miwg → news → 其他非 wi） */
export interface ContentAdWaterfallConfig {
  yanDexIndex: number;
  newsGoogleAd2Index: number;
  /** 未配置时跳过 miwgDomain1 + wgDomains3 分支 */
  miwgGoogleAd1Index?: number;
  nonWiGoogleAd1Index: number;
}

export function ContentAdWaterfall({
  position,
  config,
}: {
  position: string;
  config: ContentAdWaterfallConfig;
}): JSX.Element | null {
  const { hostName, domain } = useHostName();
  const { isTranTabAdScenario } = getCurrentAdScenario();
  const isNewsPage = isShowNews1().validMain;
  const isDexDomain = dexLinks.includes(domain);

  const miQsProps = useGoogleAd1("ad1", 0);
  const tranProps = useGoogleAd1("ad2", 0);
  const dexProps = useYanDex("ad2", config.yanDexIndex);
  const miwgProps = useGoogleAd1("ad2", config.miwgGoogleAd1Index ?? 0);
  const newsProps = useGoogleAd2("ad2", config.newsGoogleAd2Index);
  const nonWiProps = useGoogleAd1("ad2", config.nonWiGoogleAd1Index);

  if (miQsWeatherDomains.includes(hostName)) {
    return <GoogleAd1 dataPosition={position} {...miQsProps} isLazyLoad={false} />;
  }
  if (isTranTabAdScenario) {
    return <GoogleAd1 dataPosition={position} {...tranProps} isLazyLoad={false} />;
  }
  if (isDexDomain) {
    return <YanDexAd dataPosition={position} {...dexProps} />;
  }
  if (config.miwgGoogleAd1Index !== undefined && [...miwgDomain1, ...wgDomains3].includes(hostName)) {
    return <GoogleAd1 dataPosition={position} {...miwgProps} />;
  }
  if (isNewsPage) {
    return <GoogleAd2 dataPosition={position} {...newsProps} />;
  }
  if (shouldRenderNonWiAd()) {
    return (
      <GoogleAd1
        dataPosition={position}
        {...nonWiProps}
        {...([...wwwDomain1].includes(hostName) ? { isLazyLoad: false } : {})}
      />
    );
  }
  return null;
}

/** 侧边/列表通用广告瀑布流配置（miQs → tran → dex → news → 非 wi） */
export interface SideAdWaterfallConfig {
  miQsIndex: number;
  tranIndex: number;
  yanDexIndex: number;
  newsGoogleAd2Index: number;
  nonWiGoogleAd1Index: number;
}

export function SideAdWaterfall({
  position,
  config,
}: {
  position: string;
  config: SideAdWaterfallConfig;
}): JSX.Element | null {
  const { hostName, domain } = useHostName();
  const { isTranTabAdScenario } = getCurrentAdScenario();

  const miQsProps = useGoogleAd1("ad2", config.miQsIndex);
  const tranProps = useGoogleAd1("ad2", config.tranIndex);
  const dexProps = useYanDex("ad2", config.yanDexIndex);
  const newsProps = useGoogleAd2("ad2", config.newsGoogleAd2Index);
  const nonWiProps = useGoogleAd1("ad2", config.nonWiGoogleAd1Index);

  if (miQsWeatherDomains.includes(hostName)) {
    return <GoogleAd1 {...miQsProps} dataPosition={position} />;
  }
  if (isTranTabAdScenario) {
    return <GoogleAd1 dataPosition={position} {...tranProps} />;
  }
  if (dexLinks.includes(domain)) {
    return <YanDexAd dataPosition={position} {...dexProps} />;
  }
  if (isShowNews1().validMain) {
    return <GoogleAd2 dataPosition={position} {...newsProps} />;
  }
  if (shouldRenderNonWiAd()) {
    return <GoogleAd1 dataPosition={position} {...nonWiProps} />;
  }
  return null;
}

/** Daily 详情页 position-40 专用瀑布流（无 miwg 分支，nonWi 对 tran 域名 index 不同） */
export function DailyDetailTopAd({ position }: { position: string }): JSX.Element | null {
  const { hostName, domain } = useHostName();
  const { isTranTabAdScenario } = getCurrentAdScenario();

  const miQsProps = useGoogleAd1("ad1", 0);
  const tranProps = useGoogleAd1("ad2", 0);
  const dexProps = useYanDex("ad2", 1);
  const newsProps = useGoogleAd2("ad2", 1);
  const nonWiTranProps = useGoogleAd1("ad2", 0);
  const nonWiDefaultProps = useGoogleAd1("ad2", 1);

  if (miQsWeatherDomains.includes(hostName)) {
    return <GoogleAd1 dataPosition={position} {...miQsProps} isLazyLoad={false} />;
  }
  if (isTranTabAdScenario) {
    return <GoogleAd1 dataPosition={position} {...tranProps} isLazyLoad={false} />;
  }
  if (dexLinks.includes(domain)) {
    return <YanDexAd dataPosition={position} {...dexProps} />;
  }
  if (isShowNews1().validMain) {
    return <GoogleAd2 dataPosition={position} {...newsProps} />;
  }
  if (shouldRenderNonWiAd()) {
    const nonWiProps = isTranTabAdScenario ? nonWiTranProps : nonWiDefaultProps;
    return <GoogleAd1 dataPosition={position} {...nonWiProps} />;
  }
  return null;
}

/** Daily 详情页 position-37 / position-38 中间广告 */
export function DailyDetailMiddleAd({
  position,
  yanDexIndex,
  googleAd2Index,
}: {
  position: string;
  yanDexIndex: number;
  googleAd2Index: number;
}): JSX.Element | null {
  const { hostName, domain } = useHostName();
  const { isTranTabAdScenario } = getCurrentAdScenario();

  const dexProps = useYanDex("ad2", yanDexIndex);
  const newsProps = useGoogleAd2("ad2", googleAd2Index);
  const nonWiProps = useGoogleAd1("ad2", googleAd2Index);

  if (
    isTranTabAdScenario ||
    [...miQsWeatherDomains, ...domainMap["v-ls"], ...domainMap["mils"]].includes(hostName) ||
    isShowNews1().validMain
  ) {
    return null;
  }
  if (dexLinks.includes(domain)) {
    return <YanDexAd dataPosition={position} {...dexProps} />;
  }
  if (isShowNews1().validMain) {
    return <GoogleAd2 dataPosition={position} {...newsProps} />;
  }
  if (shouldRenderNonWiAd()) {
    return <GoogleAd1 dataPosition={position} {...nonWiProps} />;
  }
  return null;
}

/** Daily 列表页 position-09 广告（nonWi 判断使用 flatten 后的非 wi 域名） */
export function DailyListContentAd({ position }: { position: string }): JSX.Element | null {
  const { hostName, domain } = useHostName();
  const { isTranTabAdScenario } = getCurrentAdScenario();
  const flattenedOtherDomains = flatten(Object.values(omit(domainMap, ["wi"])));
  const shouldRenderNonWi = shouldRenderAdForDomains(hostName, flattenedOtherDomains);
  const isNewsPage = isShowNews1().validMain;
  const isDexDomain = dexLinks.includes(domain);

  const miQsProps = useGoogleAd1("ad1", 0);
  const tranProps = useGoogleAd1("ad2", 0);
  const dexProps = useYanDex("ad2", 0);
  const miwgProps = useGoogleAd1("ad2", 2);
  const newsProps = useGoogleAd2("ad2", 0);
  const nonWiProps = useGoogleAd1("ad2", 0);

  if (miQsWeatherDomains.includes(hostName)) {
    return <GoogleAd1 dataPosition={position} {...miQsProps} isLazyLoad={false} />;
  }
  if (isTranTabAdScenario) {
    return <GoogleAd1 dataPosition={position} {...tranProps} isLazyLoad={false} />;
  }
  if (isDexDomain) {
    return <YanDexAd dataPosition={position} {...dexProps} />;
  }
  if ([...miwgDomain1, ...wgDomains3].includes(hostName)) {
    return <GoogleAd1 dataPosition={position} {...miwgProps} />;
  }
  if (isNewsPage) {
    return <GoogleAd2 dataPosition={position} {...newsProps} />;
  }
  if (shouldRenderNonWi) {
    return (
      <GoogleAd1
        dataPosition={position}
        {...nonWiProps}
        {...([...wwwDomain1].includes(hostName) ? { isLazyLoad: false } : {})}
      />
    );
  }
  return null;
}
